import { IIntakeIdWithDates, FilterKey } from 'api-paths-cas';
import { MATCH_FILTER_NAMES } from 'shared-components';
import { IConfigState } from '../../../../types';
import { INTAKE_ALL_END_DATE, INTAKE_ALL_START_DATE } from '../../../../utilities/constants';
import { getActiveStageTab, getDaysAgoInEpochMilliseconds } from '../../../../utilities/helpers';
import { hasPerm, P } from '../../../../utilities/roles/permissions';
import { AuthState, UserRole } from '../../../auth/types';
import isTesting from '../../../../utilities/isTesting';
import * as api from '../../../../api/api';
import { InstitutionEnum } from '@/components/molecules/ActionsToolbar/types';

export const shouldSetUniFromState = (userRole: UserRole): boolean => {
  if (
    [UserRole.Admin, UserRole.Enroly, UserRole.Uni, UserRole.UniLite, UserRole.UniAdmin, UserRole.UniSecure].includes(
      userRole,
    )
  ) {
    return true;
  }

  return false;
};

export const createUniqueCountParams = ({
  getUniqueCountAbortController,
  auth,
  config,
  intakeId,
  headerTab,
  intakeRange,
  idToken,
}: {
  getUniqueCountAbortController: AbortController;
  auth: AuthState;
  config: IConfigState;
  intakeId: string;
  headerTab: any;
  intakeRange: IIntakeIdWithDates | undefined;
  idToken: string;
}) => {
  const params = new URLSearchParams(window.location.search);

  if (!intakeId && !isTesting) {
    return;
  }

  const uni = shouldSetUniFromState(auth.role) ? auth.uni : undefined;
  const isPathway = config.isPathway ?? false;

  const startDate = intakeRange?.startDate ?? INTAKE_ALL_START_DATE;
  const endDate = intakeRange?.endDate ?? INTAKE_ALL_END_DATE;

  const canViewIncludeStudentDropdown = !!(
    config.relatedPathways?.length &&
    hasPerm(auth.role, P.VIEW_INSTITUTION_DROPDOWN) &&
    !(
      [UserRole.UniAdmin, UserRole.Uni, UserRole.UniSecure, UserRole.UniLite].includes(auth.role) &&
      auth.pathwayAccess === 'None'
    )
  );
  getUniqueCountAbortController = new AbortController();

  let pathwayVisibility = '';
  if (canViewIncludeStudentDropdown) {
    const institutionFromParams = params.get('institution');
    const institutionFromLocalStorage = localStorage.getItem('dashboard-uni-institution-value');
    pathwayVisibility = institutionFromParams || institutionFromLocalStorage || InstitutionEnum.INSTITUTION_UNI;
  }

  return {
    abortController: getUniqueCountAbortController,
    idToken,
    stage: decodeURI(getActiveStageTab(params, headerTab)),
    ...(isPathway ? { pathway: uni } : { uni }),
    intakeId,
    pathwayVisibility,
    startDate,
    endDate,
    search: params.get('search'),
    unis: params.getAll(FilterKey.UNIVERSITY),
    studentNo: params.getAll('filter_student_number'),
    surname: params.getAll('filter_surname'),
    givenName: params.getAll('filter_given_name'),
    course: params.getAll(FilterKey.COURSE),
    faculty: params.getAll(FilterKey.FACULTY),
    nationality: params.getAll(FilterKey.NATIONALITY),
    country: params.getAll(FilterKey.COUNTRY_OF_RESIDENCE),
    applicationCountry: params.getAll(FilterKey.APPLICATION_COUNTRY),
    activityStatus: params.getAll(FilterKey.ACTIVITY_STATUS),
    idle: params.getAll('filter_IDLE'),
    complete: params.getAll(FilterKey.COMPLETE),
    incomplete: params.getAll(FilterKey.INCOMPLETE),
    fundsMaturity: params.getAll(FilterKey.FUNDS_MATURITY),
    //region: params.getAll('filter_REGION'),
    market: params.getAll(FilterKey.MARKET),
    visaStatus: params.getAll(FilterKey.VISA_STATUS),
    confidence: params.getAll(FilterKey.CONFIDENCE).filter(item => item !== 'any'),
    agent: params.getAll(FilterKey.AGENT_NAME),
    conditionalStatus: params.getAll(FilterKey.CONDITIONAL_STATUS),
    courseConditionalStatus: params.getAll(FilterKey.COURSE_CONDITIONAL_STATUS),
    evidenceRequirements: params.getAll(FilterKey.EVIDENCE_REQUIREMENTS),
    customRegion: params.getAll(FilterKey.RISK_REGION),
    tag: params.getAll(FilterKey.TAGS),
    programmableTag: params.getAll(FilterKey.PROGRAMMABLE_TAGS),
    services: params.getAll(FilterKey.SERVICES),
    immigrationProviders: params.getAll(FilterKey.IMMIGRATION_PROVIDERS),
    interview: params.getAll(FilterKey.INTERVIEW),
    recordedInterview: params.getAll(FilterKey.RECORDED_INTERVIEW),
    idleFrom: params.has(FilterKey.MAXIMUM_DAYS_IDLE) && [
      getDaysAgoInEpochMilliseconds(params.getAll(FilterKey.MAXIMUM_DAYS_IDLE).map(Number)[0]),
    ],
    idleTo: params.has(FilterKey.MINIMUM_DAYS_IDLE) && [
      getDaysAgoInEpochMilliseconds(params.getAll(FilterKey.MINIMUM_DAYS_IDLE).map(Number)[0]) - 1,
    ],
    dependants: params.getAll(FilterKey.DEPENDANTS),
    tagsMatch: params.getAll(MATCH_FILTER_NAMES.TAGS_MATCH),
    programmableTagsMatch: params.getAll(MATCH_FILTER_NAMES.PROGRAMMABLE_TAGS_MATCH),
  } as api.IGetAllParams;
};
